import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { ErrorResponse } from '@/utils/response';
import { axios, CustomAxiosResponse } from '@/utils/axios';
import { QuestionUpload } from './question-upload.model';
import { Question } from '../question/question.model';

export interface QuestionUploadStoreInterface {
  questionUploads: QuestionUpload[];
  questionUpload: QuestionUpload | null;
  error: ErrorResponse | null;
  fetchQuestionUploads(): Promise<void>;
  fetchQuestionUpload(id: number): Promise<void>;
  createQuestionUpload(params: CreateQuestionUploadParams): Promise<void>;
  sendQuestionUpload(params: SendQuestionUploadParams): Promise<void>;
}

export interface CreateQuestionUploadParams {
  subject: string;
  chapter: string;
  form: string;
  questionType: string;
  difficulty: number;
  isPrivate: boolean;
  collectionUUid: string;
  fileUploadId: number;
}

export interface SendQuestionUploadParams {
  subjectiveIds: number[];
  objectiveIds: number[];
  questionUploadId: number;
}

@Module({
  namespaced: true,
  dynamic: true,
  name: 'question-upload',
  store,
})
class QuestionUploadStore extends VuexModule {
  questionUploads: QuestionUpload[] = [];
  questionUpload: QuestionUpload | null = null;
  error: ErrorResponse | null = null;

  @Mutation
  updateQuestionUploads(params: QuestionUpload[]) {
    this.questionUploads = params;
  }

  @Mutation
  updateQuestionUpload(params: QuestionUpload) {
    this.questionUpload = params;
  }

  @Mutation
  setError(params: ErrorResponse) {
    this.error = params;
  }

  @Action
  async fetchQuestionUploads(): Promise<void> {
    this.context.commit('updateQuestionUploads', []);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(`question_upload`);

      this.context.commit('updateQuestionUploads', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchQuestionUpload(id: number): Promise<void> {
    this.context.commit('updateQuestionUpload', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(
        `question_upload/${id}`,
      );

      this.context.commit('updateQuestionUpload', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async createQuestionUpload(
    params: CreateQuestionUploadParams,
  ): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.post(`question_upload`, { ...params });
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async sendQuestionUpload(params: SendQuestionUploadParams): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.put(`question_upload`, { ...params });
    } catch (error) {
      this.context.commit('setError', error);
    }
  }
}

export async function fetchOneQuestion(
  id: number,
  isSubjective: boolean,
): Promise<Question | null> {
  let question: Question | null = null;
  try {
    const { data }: CustomAxiosResponse = await axios.get(`questions/${id}`, {
      params: {
        is_subjective: isSubjective,
      },
    });

    question = data.mathQuestion;
  } catch (error) {
    // eslint-disable no-empty
  }

  return question;
}

export default getModule(QuestionUploadStore);
